import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';

/**
 * Controlled "Import Assets from CSV" modal. All state and the upload/preview
 * network calls live in the parent (Assets.jsx) and are passed in; this
 * component renders the upload form and the preview/result view and forwards
 * events. It never sets Content-Type — the parent's FormData post handles that.
 */
export default function AssetImportModal({
  isOpen,
  onClose,
  importFile,
  setImportFile,
  importResult,
  setImportResult,
  importLoading,
  importAction,
  onSubmit,
  onPreview,
  previewGeneratedCount,
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Import Assets from CSV" className="import-modal">
      <p className="import-hint">
        CSV must include a <strong>Site ID</strong> (database integer) <em>or</em> <strong>Site Code</strong> (matching an existing site), plus <strong>Item / Type</strong>, <strong>Building ID</strong>, and <strong>Room ID</strong>. Use <strong>Equipment ID</strong> as the stable identifier (stored as <code>asset_code</code>); if missing, the system generates one automatically. <strong>Equipment Code</strong> is treated as an optional display label. <strong>Element</strong> column is required — use &quot;Electrical System (installation)&quot; or &quot;Fitments (pools, joinery, workshop equipment, etc)&quot;.
      </p>
      {!importResult ? (
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="assets-import-csv">CSV file</label>
            <input
              id="assets-import-csv"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                setImportFile(e.target.files?.[0] || null);
                setImportResult(null);
              }}
              disabled={importLoading}
            />
          </div>
          {importLoading && (
            <div className="import-progress">
              <div className="import-progress-bar" />
              <p>Processing…</p>
            </div>
          )}
          <div className="form-actions">
            <Button type="button" onClick={onClose} disabled={importLoading}>
              Cancel
            </Button>
            <Button type="button" onClick={onPreview} disabled={importLoading || !importFile}>
              {importLoading && importAction === 'preview' ? 'Previewing…' : 'Preview'}
            </Button>
            <Button type="submit" variant="primary" disabled={importLoading || !importFile}>
              {importLoading && importAction === 'import' ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="import-result">
          <p className="import-success">
            {importResult.preview ? (
              <>
                Preview parsed <strong>{importResult.previewRows?.length || 0}</strong> row{(importResult.previewRows?.length || 0) !== 1 ? 's' : ''}.
                {importResult.totalRows != null && (
                  <> ({importResult.totalRows} row{importResult.totalRows !== 1 ? 's' : ''} in file)</>
                )}
              </>
            ) : (
              <>
                Imported <strong>{importResult.imported}</strong> asset{importResult.imported !== 1 ? 's' : ''}.
                {importResult.totalRows != null && (
                  <> ({importResult.totalRows} row{importResult.totalRows !== 1 ? 's' : ''} in file)</>
                )}
              </>
            )}
          </p>
          {importResult.preview && (
            <p className="text-muted">
              Preview only: no assets were written. {previewGeneratedCount} row{previewGeneratedCount !== 1 ? 's' : ''} will auto-generate an asset code (AST-&#123;id&#125;).
            </p>
          )}
          {importResult.errors?.length > 0 && (
            <div className="import-errors">
              <strong>Errors ({importResult.errors.length}):</strong>
              <ul>
                {importResult.errors.slice(0, 20).map((err, idx) => (
                  <li key={idx}>Row {err.row}: {err.message}</li>
                ))}
                {importResult.errors.length > 20 && (
                  <li>… and {importResult.errors.length - 20} more</li>
                )}
              </ul>
            </div>
          )}
          {importResult.warnings?.length > 0 && (
            <div className="import-errors">
              <strong>Warnings ({importResult.warnings.length}):</strong>
              <ul>
                {importResult.warnings.slice(0, 20).map((warn, idx) => (
                  <li key={`warn-${idx}`}>Row {warn.row}: {warn.message}</li>
                ))}
                {importResult.warnings.length > 20 && (
                  <li>… and {importResult.warnings.length - 20} more</li>
                )}
              </ul>
            </div>
          )}
          <div className="form-actions">
            {importResult.preview && (
              <Button type="button" variant="primary" onClick={onSubmit} disabled={importLoading || !importFile}>
                Import These Rows
              </Button>
            )}
            {importResult.preview && (
              <Button type="button" onClick={() => setImportResult(null)} disabled={importLoading}>
                Back to Preview
              </Button>
            )}
            <Button
              type="button"
              variant={importResult.preview ? 'secondary' : 'primary'}
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
